import React, { useCallback } from "react";
import {
  Box,
  Chip,
  IconButton,
  InputBase,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from "@mui/material";
import UndoIcon from "@mui/icons-material/Undo";

import { useTableSort } from "./useTableSort";
import { cellKey } from "./useQuantityEdits";

const headCell = {
  fontWeight: "bold",
  whiteSpace: "nowrap",
};

const divider = "1px solid rgba(224, 224, 224, 1)";

// With a size column per size the table scrolls sideways, so the SKU stays
// pinned - otherwise the numbers lose the row they belong to. A sticky cell
// needs its own opaque background or the scrolled content shows through it.
const stickySku = {
  position: "sticky",
  left: 0,
  zIndex: 2,
  borderRight: divider,
};

const STICKY_CLASS = "pivot-sticky-sku";

// Caps the pinned column so the size columns stay on screen. The SKU list wraps
// inside it rather than being cut, and the header/footer share the width so the
// pinned column lines up.
const SKU_CELL_WIDTH = {
  width: 500,
  minWidth: 500,
  maxWidth: 500,
  whiteSpace: "normal",
  wordBreak: "break-word",
};

// Hoisted: these are applied to every cell, and rebuilding them per render is
// what emotion has to re-serialise hundreds of times in edit mode.
const cellBoxSx = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const INPUT_PROPS = {
  min: 0,
  style: { textAlign: "center", padding: "2px 0" },
};

const inputSx = {
  width: 56,
  border: "1px solid",
  borderColor: "divider",
  borderRadius: 1,
};

const inputSxEdited = {
  ...inputSx,
  borderColor: "warning.main",
  fontWeight: "bold",
  color: "warning.main",
};

// Size columns are dynamic, so their sort keys are namespaced by size name.
const SIZE_KEY = (name) => `size:${name}`;

/**
 * SKU rows x size columns, used for both the mapped and unmapped tables.
 *
 * In edit mode every cell becomes an input and all three totals (row, column,
 * grand) recompute from the typed values, so the table always adds up to what
 * is on screen.
 */
const PivotTable = ({
  rows = [],
  sizeColumns = [],
  showOrderNo = false,
  emptyText = "Nothing to show.",
  // Only meaningful where mapped and unmapped rows sit in one table, which is
  // the per-company breakdown.
  markUnmapped = false,
  editing = false,
  edits = {},
  onCellChange,
  onCellReset,
}) => {
  // What the PDF produced - shown for reference, never the editing baseline.
  const parsedOf = useCallback(
    (row, size) => (row.parsedSizes ? row.parsedSizes[size] || 0 : 0),
    []
  );

  // The value as loaded from the server: for a saved import that already
  // includes its stored corrections, so typing that number back clears the
  // unsaved change rather than snapping to the PDF figure.
  const baselineOf = useCallback((row, size) => row.sizes[size] || 0, []);

  /**
   * What the cell shows right now.
   *
   * Pending edits count whether or not the editor is open: leaving edit mode
   * only swaps inputs back to text, it does not throw the changes away. Gating
   * this on `editing` made the table show the old figures while still saving
   * the new ones.
   */
  const valueOf = useCallback(
    (row, size) => {
      const key = cellKey(row.sku, size);
      if (key in edits) return edits[key];
      return baselineOf(row, size);
    },
    [edits, baselineOf]
  );

  const rowTotal = useCallback(
    (row) =>
      sizeColumns.reduce((sum, column) => sum + valueOf(row, column.name), 0),
    [sizeColumns, valueOf]
  );

  /**
   * Sorting deliberately reads the loaded values, not the ones being typed.
   *
   * Ordering by the live figures would re-sort on every keystroke and slide the
   * row out from under the cursor; it also made the whole table re-sort on each
   * character. The order settles on the next load instead.
   */
  const getValue = useCallback(
    (row, key) => {
      if (key === "sku") return row.sku;
      if (key === "total")
        return sizeColumns.reduce(
          (sum, column) => sum + baselineOf(row, column.name),
          0
        );
      if (key.startsWith("size:")) return baselineOf(row, key.slice(5));
      return "";
    },
    [sizeColumns, baselineOf]
  );

  const isNumeric = useCallback(
    (key) => key === "total" || key.startsWith("size:"),
    []
  );

  const tieKey = useCallback((row) => row.sku, []);

  const { order, orderBy, onSort, sorted } = useTableSort({
    rows,
    getValue,
    isNumeric,
    tieKey,
    defaultOrderBy: "total",
    defaultOrder: "desc",
  });

  if (!rows.length) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
        {emptyText}
      </Typography>
    );
  }

  // Footer shows this table's own totals, from the values on screen.
  const columnTotals = {};
  sizeColumns.forEach(({ name }) => {
    columnTotals[name] = rows.reduce((sum, row) => sum + valueOf(row, name), 0);
  });
  const grandTotal = rows.reduce((sum, row) => sum + rowTotal(row), 0);

  const sortLabel = (key, children, align) => (
    <TableSortLabel
      active={orderBy === key}
      direction={orderBy === key ? order : "asc"}
      onClick={() => onSort(key)}
      sx={align === "center" ? { justifyContent: "center" } : undefined}
    >
      {children}
    </TableSortLabel>
  );

  const renderCell = (row, size) => {
    const value = valueOf(row, size);
    const parsed = parsedOf(row, size);
    const baseline = baselineOf(row, size);
    const hasUnsaved = cellKey(row.sku, size) in edits;
    const wasSavedEdit = (row.editedSizes || []).includes(size);
    const isEdited = hasUnsaved || wasSavedEdit;

    if (!editing) {
      const shown = value || "-";
      if (!isEdited) return shown;
      return (
        <Tooltip
          title={
            hasUnsaved
              ? `Changed to ${value}, not saved yet - PDF had ${parsed}`
              : `Edited - PDF had ${parsed}`
          }
        >
          <Box
            component="span"
            sx={{ color: "warning.main", fontWeight: "bold", cursor: "help" }}
          >
            {shown} {hasUnsaved ? "*" : "•"}
          </Box>
        </Tooltip>
      );
    }

    const input = (
      <InputBase
        value={value}
        onChange={(e) => onCellChange(row.sku, size, e.target.value, baseline)}
        inputProps={INPUT_PROPS}
        type="number"
        sx={isEdited ? inputSxEdited : inputSx}
      />
    );

    return (
      <Box sx={cellBoxSx}>
        {/* A Tooltip per cell is what made entering edit mode stall - only the
            handful of edited cells actually have something to say. */}
        {isEdited ? (
          <Tooltip title={`PDF had ${parsed}`}>{input}</Tooltip>
        ) : (
          input
        )}
        {hasUnsaved && (
          <Tooltip title={`Undo change (back to ${baseline})`}>
            <IconButton
              size="small"
              sx={{ ml: 0.25 }}
              onClick={() => onCellReset(row.sku, size)}
            >
              <UndoIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    );
  };

  return (
    <Paper sx={{ width: "100%", overflow: "hidden" }} variant="outlined">
      <TableContainer>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  ...headCell,
                  ...stickySku,
                  ...SKU_CELL_WIDTH,
                  // Above both the sticky header row and the sticky column.
                  zIndex: 3,
                  backgroundColor: "background.default",
                }}
              >
                {sortLabel("sku", "SKU")}
              </TableCell>
              {sizeColumns.map(({ name, sizes }) => (
                <TableCell key={name} align="center" sx={headCell}>
                  {/* A column is a size chart, so it can cover several printed
                      sizes - the tooltip says which. */}
                  <Tooltip
                    title={
                      sizes?.length
                        ? `Sizes in this chart: ${sizes.join(", ")}`
                        : "This size chart has no sizes"
                    }
                  >
                    <span>{sortLabel(SIZE_KEY(name), name, "center")}</span>
                  </Tooltip>
                </TableCell>
              ))}
              <TableCell
                align="center"
                sx={{ ...headCell, borderLeft: divider }}
              >
                {sortLabel("total", "Total", "center")}
              </TableCell>
              {showOrderNo && (
                <TableCell sx={{ ...headCell, borderLeft: divider }}>
                  Sample Order No.
                </TableCell>
              )}
            </TableRow>
          </TableHead>

          <TableBody>
            {sorted.map((row) => (
              <TableRow
                hover
                key={row.sku}
                // The pinned cell paints over the row, so it needs the hover
                // tint applied to it directly.
                sx={{ [`&:hover .${STICKY_CLASS}`]: { backgroundColor: "#fafafa" } }}
              >
                <TableCell
                  className={STICKY_CLASS}
                  sx={{
                    ...stickySku,
                    ...SKU_CELL_WIDTH,
                    backgroundColor: "background.paper",
                    ...(markUnmapped && !row.productId
                      ? { color: "error.main", fontWeight: "bold" }
                      : {}),
                  }}
                >
                  {/* A merged row can hold many SKUs. All of them are shown -
                      the column is capped in width and the list wraps onto as
                      many lines as it needs, so the size columns stay on
                      screen without hiding anything. */}
                  {(row.skus ?? [row.sku]).join(", ")}
                  {row.colors?.length ? (
                    <Chip
                      size="small"
                      variant="outlined"
                      label={row.colors.join(", ")}
                      sx={{ ml: 1, height: 18, fontSize: 11 }}
                    />
                  ) : null}
                  {/* Only a mapped SKU has a product; unmapped rows simply
                      keep the single line. */}
                  {row.productName && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", lineHeight: 1.3 }}
                    >
                      {row.productName}
                    </Typography>
                  )}
                </TableCell>
                {sizeColumns.map(({ name }) => (
                  <TableCell key={name} align="center">
                    {renderCell(row, name)}
                  </TableCell>
                ))}
                <TableCell
                  align="center"
                  sx={{ fontWeight: "bold", borderLeft: divider }}
                >
                  {rowTotal(row)}
                </TableCell>
                {showOrderNo && (
                  <TableCell sx={{ borderLeft: divider, whiteSpace: "nowrap" }}>
                    {row.sampleSubOrderNo || "-"}
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>

          <TableFooter sx={{ backgroundColor: "rgba(0, 0, 0, 0.04)" }}>
            <TableRow>
              <TableCell
                sx={{
                  ...headCell,
                  ...stickySku,
                  ...SKU_CELL_WIDTH,
                  fontSize: "0.9rem",
                  // Opaque match for the footer's translucent grey, which
                  // would otherwise let the scrolled cells show through.
                  backgroundColor: "#f5f5f5",
                }}
              >
                TOTAL
              </TableCell>
              {sizeColumns.map(({ name }) => (
                <TableCell key={name} align="center" sx={headCell}>
                  {columnTotals[name] || 0}
                </TableCell>
              ))}
              <TableCell
                align="center"
                sx={{ ...headCell, borderLeft: divider, fontSize: "0.9rem" }}
              >
                {grandTotal}
              </TableCell>
              {showOrderNo && <TableCell sx={{ borderLeft: divider }} />}
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default PivotTable;
